// Declarative Training Plan config — the guided-onboarding analog of
// pagesv2/Tutorial/tutorialSteps.js. All plan PROGRESS is derived from
// assessment history against this config (see planState.js); there is no
// persisted step pointer, so users who race ahead or disappear for weeks
// always see accurate state.

export const PLAN_LENGTH_DAYS = 10;

// Calendar days after the welcome was first shown before an unfinished plan
// quietly retires (so it can never linger for months on a semi-active user).
export const PLAN_EXPIRY_DAYS = 45;

// Completed practices at/above which a user can no longer ENTER the plan.
// Users already mid-plan keep it past this (see planState.panelVisible).
export const ENTRY_THRESHOLD = 3;

// Dormant days after a dismissal before the panel resurfaces (collapsed),
// and only while the user is still under ENTRY_THRESHOLD practices.
export const RESURFACE_AFTER_DAYS = 30;

// Days the graduation (plan-complete) panel lingers after completion before it
// auto-retires. Without this, a graduate who never clicks Finish would keep the
// panel — and the DashboardNudges suppression that comes with it — forever.
export const GRADUATION_LINGER_DAYS = 7;

// Bare Loom ids only — LoomEmbed constructs the iframe src as
// loom.com/embed/{id}, so a stored value can never point the iframe elsewhere.
export const LOOM_ID_RE = /^[a-zA-Z0-9]{16,64}$/;

// Accepts a bare Loom id or any loom.com share/embed URL; returns the id or null.
export const parseLoomId = (raw) => {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (LOOM_ID_RE.test(trimmed)) return trimmed;
  const match = trimmed.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]{16,64})/);
  return match ? match[1] : null;
};

// Slot ids are stable identifiers — they key user.trainingPlanChecks (review
// clicks) and any future analytics. Do not rename existing ids.
//
// kind: 'practice' — completed by a recording session that day
//       'review'   — completed on click (persisted in user.trainingPlanChecks);
//                    review slots NEVER gate day advancement
// completes: anyOf matchers against assessment.sessionType for that local day
// launchers: how the CTA(s) start the session; `requires` hides a launcher the
//            company's content can't support ('companyProfile' | 'qaLibrary')
// videoKey:  optional slot in company.trainingPlanVideos for a per-company
//            "Watch an example" Loom
export const SLOTS = {
  'review-message': {
    id: 'review-message',
    kind: 'review',
    title: "Read your company's message",
    description: 'Murphy grades every practice against your Company and Offerings pages. Skim them first so you know the story you are about to tell.',
    link: '/company',
    linkLabel: "Open my company's message",
    estimatedMinutes: 5,
  },
  'review-sales-cycle': {
    id: 'review-sales-cycle',
    kind: 'review',
    title: 'Learn what each conversation is for',
    description: 'Every training type mirrors a real sales moment with its own objective: an elevator pitch earns the meeting, a casual encounter tells the story. The Playbook walks the full sales cycle with examples.',
    link: '/playbook/building-message/anatomy-sales-cycle',
    linkLabel: 'Open the Playbook',
    estimatedMinutes: 5,
  },
  'messaging-defense': {
    id: 'messaging-defense',
    kind: 'practice',
    title: 'Messaging Defense',
    objective: 'Objective: know your message under pressure. Murphy plays a skeptical prospect built from your company profile.',
    estimatedMinutes: 10,
    requires: 'companyProfile',
    unavailableReason: 'Unlocks once your company profile has content. Ask your admin to fill in the Company pages.',
    launchers: [{ kind: 'prospectPrep', label: 'Start Messaging Defense' }],
    completes: [{ sessionType: 'Messaging Defense' }],
  },
  'elevator-pitch': {
    id: 'elevator-pitch',
    kind: 'practice',
    title: 'Elevator Pitch',
    objective: 'Objective: earn the meeting, not close the deal. 30 to 60 seconds.',
    estimatedMinutes: 5,
    videoKey: 'elevatorPitch',
    launchers: [{ kind: 'recording', sessionType: 'Elevator Pitch', interactive: false, label: 'Record Elevator Pitch' }],
    completes: [{ sessionType: 'Elevator Pitch' }],
  },
  'casual-encounter': {
    id: 'casual-encounter',
    kind: 'practice',
    title: 'Casual Encounter',
    objective: "Objective: tell your company's story naturally, no notes.",
    estimatedMinutes: 7,
    videoKey: 'casualEncounter',
    launchers: [{ kind: 'recording', sessionType: 'Casual Encounter', interactive: false, label: 'Record Casual Encounter' }],
    completes: [{ sessionType: 'Casual Encounter' }],
  },
  // Days 6-10 pitch slots: interactive is ENCOURAGED (default CTA), never
  // required — a standard recording completes the slot too.
  'elevator-pitch-plus': {
    id: 'elevator-pitch-plus',
    kind: 'practice',
    title: 'Elevator Pitch',
    objective: 'Objective: stay composed when a prospect interrupts. Try interactive mode and Murphy will ask a live question. A standard recording counts too.',
    estimatedMinutes: 5,
    videoKey: 'elevatorPitch',
    encourageInteractive: true,
    launchers: [
      { kind: 'recording', sessionType: 'Elevator Pitch', interactive: true, label: 'Record Interactive' },
      { kind: 'recording', sessionType: 'Elevator Pitch', interactive: false, label: 'Standard', secondary: true },
    ],
    completes: [{ sessionType: 'Elevator Pitch' }],
  },
  'casual-encounter-plus': {
    id: 'casual-encounter-plus',
    kind: 'practice',
    title: 'Casual Encounter',
    objective: 'Objective: keep the story flowing while Murphy pushes back live. Interactive is worth trying; standard counts too.',
    estimatedMinutes: 7,
    videoKey: 'casualEncounter',
    encourageInteractive: true,
    launchers: [
      { kind: 'recording', sessionType: 'Casual Encounter', interactive: true, label: 'Record Interactive' },
      { kind: 'recording', sessionType: 'Casual Encounter', interactive: false, label: 'Standard', secondary: true },
    ],
    completes: [{ sessionType: 'Casual Encounter' }],
  },
  // Either defense type completes the slot (anyOf), so the day can never
  // un-complete if an admin later adds/removes the Q&A library.
  'defense': {
    id: 'defense',
    kind: 'practice',
    title: 'Defense practice',
    objective: 'Objective: handle real pushback without flinching. Objection Handling or Messaging Defense, either counts.',
    estimatedMinutes: 10,
    unavailableReason: 'Unlocks once your company has a Q&A library or a filled-in company profile.',
    launchers: [
      { kind: 'qaSession', label: 'Objection Handling', requires: 'qaLibrary' },
      { kind: 'prospectPrep', label: 'Messaging Defense', requires: 'companyProfile' },
    ],
    completes: [{ sessionType: 'Objection Handling' }, { sessionType: 'Messaging Defense' }],
  },
};

// The 10 plan days. Day 1 reads the message, gets quizzed on it, then pitches
// it; day 2 teaches what each conversation is for before Casual Encounter
// appears; days 3-5 are the full daily trio; days 6-10 encourage interactive.
const TRIO = ['elevator-pitch', 'casual-encounter', 'defense'];
const TRIO_PLUS = ['elevator-pitch-plus', 'casual-encounter-plus', 'defense'];
export const PLAN_DAYS = [
  ['review-message', 'messaging-defense', 'elevator-pitch'],
  ['review-sales-cycle', 'elevator-pitch', 'casual-encounter'],
  TRIO,
  TRIO,
  TRIO,
  TRIO_PLUS,
  TRIO_PLUS,
  TRIO_PLUS,
  TRIO_PLUS,
  TRIO_PLUS,
];

// Graduation state shown once the 10 days are complete: the plan hands off to
// an ongoing rhythm plus the two highest-leverage ways to keep using Murphy on
// real deals. Each recommendation's launchers reuse the dashboard handlers.
export const GRADUATION = {
  title: "You've completed your Training Plan!",
  intro: "Nice work on your first two weeks. This is where the habit starts, so here's how to keep sharpening your message and put Murphy to work on real deals.",
  recommendations: [
    {
      id: 'weekly-rhythm',
      icon: '\u{1F5D3}️', // calendar
      title: 'Keep a weekly rhythm',
      body: 'Record one Elevator Pitch and one Casual Encounter each week to keep your story sharp.',
      launchers: [
        { kind: 'recording', sessionType: 'Elevator Pitch', interactive: false, label: 'Elevator Pitch' },
        { kind: 'recording', sessionType: 'Casual Encounter', interactive: false, label: 'Casual Encounter', secondary: true },
      ],
    },
    {
      id: 'meeting-prep',
      icon: '\u{1F3AF}', // target
      title: 'Prep for a specific meeting',
      body: "Before an important call, run a quick pitch as if that exact person is in front of you. Use their name and what you know about them, so you walk in warm.",
      launchers: [
        { kind: 'recording', sessionType: 'Elevator Pitch', interactive: false, label: 'Practice a pitch' },
      ],
    },
    {
      id: 'document-defense',
      icon: '\u{1F4C4}', // page
      title: 'Defend the documents you present',
      body: "Upload any document you'll present to a client or prospect, then let Murphy quiz you so you can defend every line.",
      launchers: [
        { kind: 'docDefense', label: 'Upload a document' },
      ],
    },
  ],
};

// Compact phase summary rendered in the welcome modal.
export const PLAN_PHASES = [
  { label: 'Day 1', summary: "Read your company's message, get quizzed on it by Murphy, then record your first Elevator Pitch" },
  { label: 'Day 2', summary: 'Learn what each conversation is for, then pitch and tell your story with a Casual Encounter' },
  { label: 'Days 3-5', summary: 'The daily set: Elevator Pitch, Casual Encounter, and a defense session' },
  { label: 'Days 6-10', summary: 'Same set, and Murphy starts interrupting when you try interactive mode' },
];
